import { motion } from "framer-motion";
import { Download, Lock, Pause, Play, ShoppingCart } from "lucide-react";
import { usePlayer } from "@/context/PlayerContext";
import { Waveform } from "@/components/Waveform";
import { fileUrl } from "@/lib/api";

export const BeatCard = ({ beat, list, index = 0 }) => {
  const { playBeat, isCurrent, playing, toggle, progress, duration, openLicense, openFreeDownload } = usePlayer();
  const active = isCurrent(beat);
  const price = beat.prices?.mp3 ?? 17.99;
  const isFree = !!beat.free_download;
  const isSold = !!beat.sold_exclusive;

  return (
    <motion.article
      initial={{ opacity: 0, y: 24 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-40px" }}
      transition={{ duration: 0.6, delay: (index % 3) * 0.08, ease: [0.22, 1, 0.36, 1] }}
      whileHover={{ y: -6 }}
      className={`group overflow-hidden rounded-2xl border bg-[#141419] transition-colors duration-300 ${
        active ? "border-emerald-300/50" : "border-white/10 hover:border-white/25"
      }`}
      data-testid={`beat-card-${beat.id}`}
    >
      <div className="relative aspect-square overflow-hidden">
        <img
          src={fileUrl(beat.cover_url)}
          alt={beat.title}
          loading="lazy"
          className="spotlight-img h-full w-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-transparent to-transparent" />
        <button
          data-testid={`beat-play-button-${beat.id}`}
          onClick={() => (active ? toggle() : playBeat(beat, list))}
          className={`absolute inset-0 m-auto flex h-14 w-14 items-center justify-center rounded-full transition-all duration-300 ${
            active
              ? "bg-emerald-300 text-black opacity-100"
              : "bg-black/60 text-white opacity-0 backdrop-blur-md group-hover:opacity-100"
          }`}
        >
          {active && playing ? <Pause size={22} fill="currentColor" /> : <Play size={22} fill="currentColor" className="ml-0.5" />}
        </button>
        <div className="absolute left-3 top-3 flex gap-2">
          <span className="rounded-full bg-black/60 px-3 py-1 font-mono text-[11px] uppercase tracking-wider text-emerald-200 backdrop-blur-md">
            {beat.bpm} BPM
          </span>
          {beat.musical_key && (
            <span className="rounded-full bg-black/60 px-3 py-1 font-mono text-[11px] uppercase tracking-wider text-zinc-300 backdrop-blur-md">
              {beat.musical_key}
            </span>
          )}
          {isFree && !isSold && (
            <span className="rounded-full bg-emerald-300 px-3 py-1 font-mono text-[11px] font-bold uppercase tracking-wider text-black" data-testid={`beat-free-badge-${beat.id}`}>
              Free
            </span>
          )}
          {isSold && (
            <span className="flex items-center gap-1 rounded-full bg-red-500/90 px-3 py-1 font-mono text-[11px] font-bold uppercase tracking-wider text-white" data-testid={`beat-sold-badge-${beat.id}`}>
              <Lock size={10} /> SOLD — Exclusive
            </span>
          )}
        </div>
      </div>

      <div className="space-y-3 p-5">
        <div>
          <h3 className="font-heading text-lg font-bold text-white">{beat.title}</h3>
          <p className="mt-1 truncate text-xs text-zinc-500">{beat.tags?.join(" ")}</p>
        </div>
        <Waveform
          seed={beat.id}
          progress={active && duration ? progress / duration : 0}
          bars={40}
          height={32}
          live={active && playing}
        />
        <div className="flex items-center justify-between gap-2 pt-1">
          <span className="font-mono text-sm text-zinc-400">
            {isSold ? (
              <span className="font-bold text-red-400">EXCLUSIVELY SOLD</span>
            ) : isFree ? (
              <span className="font-bold text-emerald-200">FREE MP3</span>
            ) : (
              <>from <span className="font-bold text-white">${price.toFixed(2)}</span></>
            )}
          </span>
          <div className="flex items-center gap-2">
            {isSold ? (
              <span
                data-testid={`beat-sold-button-${beat.id}`}
                className="flex cursor-not-allowed items-center gap-2 rounded-full border border-red-500/40 px-4 py-2 text-xs font-bold uppercase tracking-wider text-red-400/70"
              >
                <Lock size={13} /> Sold
              </span>
            ) : (
              <>
                {isFree && (
                  <button
                    data-testid={`beat-free-download-button-${beat.id}`}
                    onClick={() => openFreeDownload(beat)}
                    className="flex items-center gap-2 rounded-full border border-emerald-300/50 px-4 py-2 text-xs font-bold uppercase tracking-wider text-emerald-200 transition-all hover:bg-emerald-300/10"
                  >
                    <Download size={13} /> Free
                  </button>
                )}
                <button
                  data-testid={`beat-buy-lease-button-${beat.id}`}
                  onClick={() => openLicense(beat)}
                  className="flex items-center gap-2 rounded-full bg-emerald-300 px-4 py-2 text-xs font-bold uppercase tracking-wider text-black transition-all hover:bg-emerald-200 hover:shadow-[0_0_24px_rgba(110,231,183,0.35)]"
                >
                  <ShoppingCart size={13} /> Buy
                </button>
              </>
            )}
          </div>
        </div>
      </div>
    </motion.article>
  );
};
